import type { ReactNode } from 'react'
import { BRAND } from '../lib/brand.js'
import { cn } from '../lib/cn.js'
import { Icon, type IconName } from './Icon.js'
import { Lockup } from './Wordmark.js'

/**
 * The shell behind every signed-out screen: sign in, the two halves of a
 * password reset, and accepting an invitation.
 *
 * A split rather than the card on a dark field it replaced. The card was one
 * 384px column in the middle of a 27-inch office monitor with nothing either
 * side of it, and the form inside it was the same white as the card, so the
 * shadow was doing all the work of saying where the screen was. Giving the
 * brand its own half means the form gets a whole column of white to itself and
 * the product gets somewhere to say what it is — which matters here, because
 * this is the one screen someone sees before they have any context.
 *
 * Below `lg` the panel collapses to a band across the top. A phone has no room
 * for a second column, and a technician standing in a driveway wants the two
 * fields and the button, not the pitch.
 *
 * Everything inside the form column is the normal kit at normal sizes. The
 * screen reads as part of the product, not as a landing page bolted to it.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string
  subtitle?: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-full flex-col bg-white lg:flex-row">
      <BrandPanel />

      <main
        className={cn(
          'flex min-w-0 flex-1 flex-col justify-center px-6 py-10 sm:px-10',
          'pb-[calc(var(--safe-bottom)+2.5rem)]',
        )}
      >
        <div className="mx-auto w-full max-w-[380px]">
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-brand-800">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-ink-500">{subtitle}</p> : null}
          <div className="mt-6">{children}</div>
        </div>
      </main>
    </div>
  )
}

/**
 * What the product is, in three lines a dispatcher would recognise.
 *
 * Kept to things that exist. A sign-in screen promising features the build
 * does not have is the first thing a client notices and the last thing they
 * forget.
 */
const HIGHLIGHTS: { icon: IconName; title: string; detail: string }[] = [
  {
    icon: 'schedule',
    title: 'The whole day on one board',
    detail: 'Every van, every booking, and what is running late.',
  },
  {
    icon: 'jobs',
    title: 'One card for the job',
    detail: 'Client, site, services, files and history in the same place.',
  },
  {
    icon: 'mic',
    title: 'Reports finished on site',
    detail: 'Dictate the findings and get a signature before you drive off.',
  },
]

function BrandPanel() {
  return (
    <>
      {/* The phone: a band, not a column. Clears the notch on the native
          build, which is why the top padding reads the safe-area inset. */}
      <div className="auth-field px-6 pt-[calc(var(--safe-top)+1.25rem)] pb-5 lg:hidden">
        <Lockup onDark className="w-[180px]" />
      </div>

      <div className="auth-field hidden w-[44%] max-w-[600px] shrink-0 flex-col justify-between p-12 lg:flex">
        <Lockup onDark tagline className="w-[300px]" />

        <div className="max-w-[400px] py-10">
          <p className="font-display text-[28px] leading-tight font-extrabold tracking-tight text-white">
            Jobs, scheduling and invoicing, built for the field.
          </p>

          <ul className="mt-8 flex flex-col gap-6">
            {HIGHLIGHTS.map((highlight) => (
              <li key={highlight.title} className="flex gap-3">
                <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-white/10 text-white ring-1 ring-inset ring-white/15">
                  <Icon name={highlight.icon} className="size-4" />
                </span>
                <span>
                  <span className="block text-sm font-medium text-white">{highlight.title}</span>
                  <span className="mt-0.5 block text-[13px] leading-snug text-sky/75">
                    {highlight.detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-sky/60">{BRAND.fullName}</p>
      </div>
    </>
  )
}

const NOTICE_TONES = {
  info: 'bg-ink-50 text-ink-600 ring-ink-200',
  good: 'bg-brand-50 text-brand-700 ring-brand-200',
  error: 'bg-red-50 text-red-700 ring-red-200',
} as const

/**
 * A line above the form explaining why you are looking at it.
 *
 * Good is brand navy rather than green, per the colour rule in `ui.tsx`: green
 * means "do this", and a notice is not asking for anything.
 */
export function AuthNotice({
  tone = 'info',
  icon,
  children,
}: {
  tone?: keyof typeof NOTICE_TONES
  icon?: IconName
  children: ReactNode
}) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md px-3 py-2 text-xs leading-snug ring-1 ring-inset',
        NOTICE_TONES[tone],
      )}
    >
      {icon ? <Icon name={icon} className="mt-px size-4" /> : null}
      <span>{children}</span>
    </p>
  )
}
